/**
 * THE source of truth for which products each deployment publishes and at which
 * URL.
 *
 * The header dropdown used to build its product links from
 * `ProductService.getMarketAnalytics()` (CMS ids + CMS short names run through
 * `langSelector`), while the footer carried its own hand-maintained arrays in
 * `footer-links.ts`. The two drifted: the same product was reachable at several
 * spellings, the URL a menu emitted was not the one the sitemap declared
 * canonical, and a product retired on one country stayed linkable on another.
 *
 * Everything that emits a `/products/...` URL now resolves it here, so header,
 * footer, in-page cards, canonical tags and hreflang alternates cannot disagree.
 * Content (titles, descriptions, images) still comes from the CMS — only the
 * URL, the label key and the per-country availability are hardcoded.
 *
 * Derived from `9.1.2026 Products - Correct URL.xlsx`. CO is not in that sheet;
 * its catalog matches what the CO header already showed (Market Analytics only),
 * which also stops CO from linking at MX's CMS ids on the `siila.com.co` domain.
 *
 * Pure data + pure functions — no Angular, no `window` — so it is safe to import
 * from route config and from SSR. `ProductCatalogService` is the injectable
 * wrapper that turns entries into localized, ready-to-use links.
 */

/**
 * What a catalog entry IS, independent of the id it happens to carry on a given
 * deployment. The same product is 101 on BR and 106 on LATAM, so id-list checks
 * (`id === 103 || id === 105`) drift every time a country is added; callers ask
 * for the kind instead.
 */
export type ProductKind =
  | "market-analytics"
  | "grocs"
  | "indices"
  | "investor-survey"
  | "spot"
  | "academy"
  | "fii-data-pro"
  | "fibra-analytics";

export interface ProductCatalogEntry {
  /**
   * CMS id behind the `products/:id/:name` detail route, or `null` for a
   * statically routed page (`/marketanalytics`, `/SPOT`, `/academy`,
   * `/products/fii-data-pro`, `/products/fibra-analytics`).
   */
  id: number | null;
  kind: ProductKind;
  /** i18n key for the visible label — shared by the header menu and the footer. */
  labelKey: string;
  /** Material icon rendered beside the label in the header/sidenav dropdown. */
  icon: string;
  /**
   * `:name` slug per language for id-driven entries, stored RAW (`índices`, not
   * `%C3%ADndices`) — the same convention the existing link builders use; the
   * browser and `UrlCanonicalService` handle percent-encoding. Absent on static
   * entries, which use {@link path}.
   */
  slugs?: { [lang: string]: string };
  /** Fixed path for static entries; identical in every language. */
  path?: string;
  /** Casing class the footer/menu anchor carries, preserved per entry. */
  linkClass: string;
}

/**
 * A URL that must move to a DIFFERENT product than the one its id names.
 *
 * Keyed on id **and** slug because the id alone is ambiguous: on LATAM
 * `/products/105/investor-survey` belongs to 108 while
 * `/products/105/siila-mexico-index` belongs to 107, and on MX id 105 is a live
 * product yet `/products/105/siila-mexico-index` still has to move to 104.
 *
 * Only CROSS-ID moves live here. A URL that names a valid id with a stale slug
 * (`/products/102/siila-brasil-index` on BR) needs no entry: the catalog already
 * knows 102's canonical slug, so `resolveProductEntry` returns the right entry
 * and the caller redirects to the canonical spelling.
 */
export interface ProductRedirect {
  fromId: number;
  /** Already normalized — lowercase, no diacritics. See `normalizeProductSlug`. */
  fromSlug: string;
  toId: number;
}

const MARKET_ANALYTICS: ProductCatalogEntry = {
  id: null,
  kind: "market-analytics",
  labelKey: "footer.headers.marketAnalytics",
  icon: "bar_chart",
  path: "/marketanalytics",
  linkClass: "uppercase"
};

const SPOT: ProductCatalogEntry = {
  id: null,
  kind: "spot",
  labelKey: "footer.headers.siilaSpot",
  icon: "location_pin",
  path: "/SPOT",
  linkClass: "uppercase"
};

const ACADEMY: ProductCatalogEntry = {
  id: null,
  kind: "academy",
  labelKey: "footer.headers.academy",
  icon: "school",
  path: "/academy",
  linkClass: "uppercase"
};

const FII_DATA_PRO: ProductCatalogEntry = {
  id: null,
  kind: "fii-data-pro",
  labelKey: "footer.headers.fii",
  icon: "corporate_fare",
  path: "/products/fii-data-pro",
  linkClass: "lowercase"
};

const FIBRA_ANALYTICS: ProductCatalogEntry = {
  id: null,
  kind: "fibra-analytics",
  labelKey: "footer.headers.fibra",
  icon: "corporate_fare",
  path: "/products/fibra-analytics",
  linkClass: "lowercase"
};

/**
 * Per-deployment product list, in the order it is rendered in both the header
 * dropdown and the footer's Products column.
 */
export const PRODUCT_CATALOG: {
  [platformId: string]: ProductCatalogEntry[];
} = {
  // BR — PT is the default language, EN lives under /lang/en.
  br: [
    MARKET_ANALYTICS,
    {
      id: 101,
      kind: "grocs",
      labelKey: "footer.headers.grocs",
      icon: "public",
      slugs: { pt: "grocs", en: "grocs" },
      linkClass: "uppercase"
    },
    {
      id: 102,
      kind: "indices",
      labelKey: "footer.headers.indices",
      icon: "show_chart",
      slugs: { pt: "índices", en: "indices" },
      linkClass: "uppercase"
    },
    {
      id: 103,
      kind: "investor-survey",
      labelKey: "footer.headers.survey",
      icon: "assignment",
      slugs: { pt: "investor-survey", en: "investor-survey" },
      linkClass: "uppercase"
    },
    SPOT,
    ACADEMY,
    FII_DATA_PRO
  ],

  // MX — ES is the default language, EN lives under /lang/en.
  mx: [
    MARKET_ANALYTICS,
    FIBRA_ANALYTICS,
    {
      id: 104,
      kind: "indices",
      labelKey: "footer.headers.indices",
      icon: "show_chart",
      slugs: { es: "índices", en: "indices" },
      linkClass: "uppercase"
    },
    {
      id: 105,
      kind: "investor-survey",
      labelKey: "footer.headers.survey",
      icon: "assignment",
      slugs: { es: "investor-survey", en: "investor-survey" },
      linkClass: "uppercase"
    },
    SPOT,
    ACADEMY
  ],

  // LATAM — EN is the default language, ES lives under /lang/es.
  latam: [
    MARKET_ANALYTICS,
    FII_DATA_PRO,
    FIBRA_ANALYTICS,
    {
      id: 107,
      kind: "indices",
      labelKey: "footer.headers.indices",
      icon: "show_chart",
      slugs: { en: "indices", es: "indices" },
      linkClass: "uppercase"
    },
    {
      id: 108,
      kind: "investor-survey",
      labelKey: "footer.headers.survey",
      icon: "assignment",
      slugs: { en: "investor-survey", es: "investor-survey" },
      linkClass: "uppercase"
    },
    {
      id: 106,
      kind: "grocs",
      labelKey: "footer.headers.grocs",
      icon: "public",
      slugs: { en: "grocs", es: "grocs" },
      linkClass: "uppercase"
    },
    SPOT,
    ACADEMY
  ],

  // CO — not covered by the products sheet. Matches what the CO header already
  // rendered; every `/products/<id>/<slug>` URL therefore answers 404 on CO.
  co: [MARKET_ANALYTICS]
};

/** Cross-id moves, per deployment. See {@link ProductRedirect}. */
export const PRODUCT_REDIRECTS: { [platformId: string]: ProductRedirect[] } = {
  br: [{ fromId: 105, fromSlug: "investor-survey", toId: 103 }],
  mx: [
    { fromId: 105, fromSlug: "siila-mexico-index", toId: 104 },
    { fromId: 106, fromSlug: "investor-survey", toId: 105 }
  ],
  latam: [
    { fromId: 101, fromSlug: "grocs", toId: 106 },
    { fromId: 102, fromSlug: "indices", toId: 107 },
    { fromId: 105, fromSlug: "investor-survey", toId: 108 },
    { fromId: 105, fromSlug: "siila-mexico-index", toId: 107 },
    { fromId: 106, fromSlug: "investor-survey", toId: 108 }
  ],
  co: []
};

/**
 * Fold a language code to the key the catalog stores. `en-mx` is the MX English
 * bundle and `pt-br` the BR Portuguese one; both address the same slug set as
 * `en` / `pt`.
 */
export function normalizeCatalogLang(lang: string): string {
  if (!lang) {
    return "en";
  }
  if (lang === "en-mx") {
    return "en";
  }
  if (lang === "pt-br") {
    return "pt";
  }
  return lang;
}

/**
 * Reduce a `:name` segment to a comparable key: percent-decoded, lowercased and
 * stripped of diacritics, so `%C3%ADndices`, `Índices` and `indices` all match
 * the one `fromSlug` spelling stored above. Deliberately tolerant on SPELLING
 * only — it never merges two different slugs.
 */
export function normalizeProductSlug(rawSlug: string): string {
  if (!rawSlug) {
    return "";
  }
  let slug = rawSlug;
  try {
    slug = decodeURIComponent(slug);
  } catch (e) {}
  return slug
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

/** Every entry this deployment publishes, in render order. */
export function getProductCatalog(platformId: string): ProductCatalogEntry[] {
  return PRODUCT_CATALOG[platformId] || [];
}

/** The id-driven entry with this id on this deployment, or `null`. */
export function findCatalogEntryById(
  platformId: string,
  id: number
): ProductCatalogEntry {
  const match = getProductCatalog(platformId).find(entry => entry.id === id);
  return match || null;
}

/** The first entry of this kind on this deployment, or `null`. */
export function findCatalogEntryByKind(
  platformId: string,
  kind: ProductKind
): ProductCatalogEntry {
  const match = getProductCatalog(platformId).find(
    entry => entry.kind === kind
  );
  return match || null;
}

/**
 * Resolve a requested `products/:id/:name` pair to the entry that should answer
 * it, or `null` when this deployment publishes no such product (the caller then
 * answers 404).
 *
 * Redirects are consulted FIRST and the catalog second, because a redirect may
 * point away from an id that is itself live here — MX publishes 105, yet
 * `/products/105/siila-mexico-index` still belongs to 104.
 */
export function resolveProductEntry(
  platformId: string,
  id: number,
  rawSlug: string
): ProductCatalogEntry {
  if (id == null || isNaN(id)) {
    return null;
  }
  const slug = normalizeProductSlug(rawSlug);
  const redirect = (PRODUCT_REDIRECTS[platformId] || []).find(
    rule => rule.fromId === id && rule.fromSlug === slug
  );
  return findCatalogEntryById(platformId, redirect ? redirect.toId : id);
}

/**
 * The entry's suffixless path for `lang`, WITHOUT a leading slash and without
 * the `/lang/<code>` segment — `buildLocalizedPath` owns that decision. Falls
 * back to the entry's first declared slug so a language the sheet does not cover
 * still yields a resolvable URL rather than `undefined`.
 */
export function getProductBasePath(
  entry: ProductCatalogEntry,
  lang: string
): string {
  if (!entry) {
    return null;
  }
  if (entry.id == null) {
    return entry.path.replace(/^\/+/, "");
  }
  const key = normalizeCatalogLang(lang);
  const slug = entry.slugs[key] || entry.slugs[Object.keys(entry.slugs)[0]];
  return `products/${entry.id}/${slug}`;
}

/**
 * Pull the `(id, slug)` pair out of a `/products/<id>/<slug>` path, or `null`
 * when the path is not an id-driven product URL (`/products`,
 * `/products/fii-data-pro`, anything else). Any `/lang/<code>` tail and query
 * string are ignored.
 */
export function parseProductDetailPath(
  path: string
): { id: number; slug: string } {
  if (!path) {
    return null;
  }
  const segments = path
    .split("?")[0]
    .split("#")[0]
    .replace(/^\/+/, "")
    .split("/");
  if (segments[0] !== "products" || !/^\d+$/.test(segments[1] || "")) {
    return null;
  }
  return { id: +segments[1], slug: segments[2] || "" };
}
